// PortfolioRecentPosts — the Recent Posts block inside the Social Accounts
// section (EPD-11833), ported from the media-kit-v2 prototype's PostsRow. A
// horizontal, scroll-snapped row of recent-post cards for the active platform,
// sourced from channel.getSocialStats `posts`. Each card links out to the
// original post when it has a safe http(s) permalink (EPD-12092). The
// prototype's upload slot is an authoring concern, out of scope here. Themed
// via --pf-*.

import { getPlatformLabel } from "@/components/channel/platform-utils";
import type { SocialStatsEntry } from "@/lib/queries/channel";
import { isHttpUrl } from "@/lib/portfolio/collaborations";
import { fmtCount } from "@/lib/portfolio/metric-catalog";
import { proxyImageSrcSet, proxyImageUrl } from "@/lib/image-url";
import { postImageAlt, postLinkLabels } from "@/lib/portfolio/post-a11y";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Eye,
  Heart,
} from "lucide-react";
import { type ReactNode, useRef, useState } from "react";

export type SocialPost = NonNullable<
  NonNullable<SocialStatsEntry["stats"]>["posts"]
>[number];

const MAX_POSTS = 12;

/** Card width in CSS px; thumbnails are requested at ~1x and ~2x of it. */
const CARD_WIDTH = 180;
/** Worker width-ladder rungs covering 1x (180 → 256) and 2x (360 → 512). */
const THUMB_WIDTHS = [256, 512] as const;

/**
 * Cards in the first screenful of the row (about two on a phone, four or five
 * on desktop). When the block sits under the header these load eagerly, the
 * first at high priority (EPD-12468): lazy loading held the page's largest
 * image back until layout. The rest of the row stays lazy.
 */
export const EAGER_POST_COUNT = 4;

function thumbnailOf(post: SocialPost): string | null {
  // `||`, not `??`: an empty-string image falls through, matching the
  // collab thumbnail lookup in lib/portfolio/post-thumbnail.ts.
  return post.image || post.thumbnailUrl || null;
}

/** The post's permalink, only when it is an http(s) URL. */
export function postHref(post: SocialPost): string | null {
  const link = post.link?.trim();
  return link && isHttpUrl(link) ? link : null;
}

/** The account's profile URL, only when it is an http(s) URL (EPD-12128). */
export function profileHref(entry: SocialStatsEntry): string | null {
  const url = entry.profileUrl?.trim();
  return url && isHttpUrl(url) ? url : null;
}

function renderablePosts(entry: SocialStatsEntry): SocialPost[] {
  return (entry.stats?.posts ?? [])
    .filter((p) => thumbnailOf(p))
    .slice(0, MAX_POSTS);
}

/** Whether the entry has any recent post with a thumbnail to show. */
export function hasRecentPosts(entry: SocialStatsEntry): boolean {
  return renderablePosts(entry).length > 0;
}

interface PortfolioRecentPostsProps {
  entry: SocialStatsEntry;
  /**
   * The creator's name, for the alt text of thumbnails that aren't links
   * (EPD-12471). A linked thumbnail is decorative.
   */
  creatorName?: string;
  /** Load the first row eagerly, the first image at high priority. */
  prioritizeImages?: boolean;
}

export function PortfolioRecentPosts({
  entry,
  creatorName,
  prioritizeImages = false,
}: PortfolioRecentPostsProps) {
  const scrollerRef = useRef<HTMLUListElement>(null);
  // Thumbnails are hotlinked from third-party social CDNs, whose URLs can expire
  // or refuse cross-origin loads; drop a card whose image fails rather than
  // leaving a broken slot.
  const [failedKeys, setFailedKeys] = useState<Set<string | number>>(
    () => new Set(),
  );
  const posts = renderablePosts(entry);
  if (posts.length === 0) return null;
  const platformLabel = getPlatformLabel(entry.platform);
  const profileUrl = profileHref(entry);
  const linkLabels = postLinkLabels(
    platformLabel,
    posts.map((post) => post.postedTs),
  );

  const scrollByPage = (direction: number) => {
    const el = scrollerRef.current;
    if (el)
      el.scrollBy({ left: direction * el.clientWidth, behavior: "smooth" });
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 style={{ margin: 0 }}>Recent Posts</h3>
        {/* Opens the selected account's profile on the platform (EPD-12128);
            omitted when the channel has no safe profile URL. */}
        {profileUrl && (
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[12px] font-medium no-underline hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            style={{ color: "var(--pf-muted)" }}
          >
            View profile
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            <span className="sr-only"> on {platformLabel}</span>
          </a>
        )}
      </div>
      <div className="relative">
        {/* `relative` makes the scroller the containing block of the cards'
            absolutely-positioned sr-only labels; without it they escape the
            scroll clip and widen the page into a horizontal scroll on phones
            (seen while verifying EPD-12294). */}
        <ul
          ref={scrollerRef}
          className="relative m-0 flex list-none gap-2.5 overflow-x-auto p-0 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{ scrollSnapType: "x mandatory" }}
        >
          {posts.map((post, i) => {
            const key = post.id ?? i;
            const src = thumbnailOf(post);
            const href = postHref(post);
            const eager = prioritizeImages && i < EAGER_POST_COUNT;
            return (
              <li
                key={key}
                hidden={failedKeys.has(key)}
                className="shrink-0 overflow-hidden rounded-2xl border"
                style={{
                  width: CARD_WIDTH,
                  borderColor: "var(--pf-border)",
                  background: "var(--pf-bg)",
                  scrollSnapAlign: "start",
                }}
              >
                <PostLink href={href} label={linkLabels[i]}>
                  <div
                    className="relative overflow-hidden"
                    style={{
                      aspectRatio: "3 / 4",
                      background: "var(--pf-surface)",
                    }}
                  >
                    {src && (
                      <img
                        // Route through the image proxy, resized for the 180px
                        // card at 1x/2x (EPD-12468); GCS-stored thumbnails are
                        // resized on the app origin, social-CDN URLs pass
                        // through unchanged (and get no srcset).
                        src={proxyImageUrl(src, 360) ?? src}
                        srcSet={proxyImageSrcSet(src, THUMB_WIDTHS)}
                        sizes={`${CARD_WIDTH}px`}
                        // Says whose post it is (EPD-12471). The caption is
                        // left out: it is shown right below the image. Inside
                        // a post link the image is decorative: its alt would
                        // start every link's name with the same words, and
                        // the link's own label already says it's a post.
                        alt={
                          href
                            ? ""
                            : postImageAlt({ platformLabel, creatorName })
                        }
                        loading={eager ? undefined : "lazy"}
                        fetchPriority={eager && i === 0 ? "high" : undefined}
                        // Don't leak the portfolio URL to the third-party CDN.
                        referrerPolicy="no-referrer"
                        onError={() =>
                          setFailedKeys((prev) => {
                            if (prev.has(key)) return prev;
                            const next = new Set(prev);
                            next.add(key);
                            return next;
                          })
                        }
                        className="h-full w-full object-cover"
                      />
                    )}
                  </div>
                  <div className="space-y-1.5 px-3 py-2.5">
                    {post.caption && (
                      <p
                        className="line-clamp-2 text-[12.5px] font-medium leading-snug"
                        style={{ color: "var(--pf-heading)" }}
                      >
                        {post.caption}
                      </p>
                    )}
                    <div
                      className="flex items-center gap-3 text-[11px]"
                      style={{ color: "var(--pf-muted)" }}
                    >
                      {post.likeCount != null && (
                        <span className="inline-flex items-center gap-1">
                          <Heart className="h-3 w-3" aria-hidden />{" "}
                          {fmtCount(post.likeCount)}
                          <span className="sr-only"> likes</span>
                        </span>
                      )}
                      {/* EPD-12473: 0 is hidden on every platform, like a missing count,
                          because the legacy Instagram feed sends 0 as an unset default. */}
                      {post.viewCount != null && post.viewCount > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Eye className="h-3 w-3" aria-hidden />{" "}
                          {fmtCount(post.viewCount)}
                          <span className="sr-only"> views</span>
                        </span>
                      )}
                    </div>
                  </div>
                </PostLink>
              </li>
            );
          })}
        </ul>
        {posts.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous posts"
              onClick={() => scrollByPage(-1)}
              className="absolute left-0 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              style={{ background: "rgba(15,19,17,0.55)" }}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Next posts"
              onClick={() => scrollByPage(1)}
              className="absolute right-0 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              style={{ background: "rgba(15,19,17,0.55)" }}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </>
        )}
      </div>
    </section>
  );
}

interface PostLinkProps {
  href: string | null;
  label: string;
  children: ReactNode;
}

/**
 * Opens the original post in a new tab; without a safe permalink the card is
 * display-only. A native anchor keeps touch/trackpad scrolling of the row
 * intact (there's no custom drag handling to fight with), and draggable={false}
 * stops a mouse drag from starting a link drag instead. The label is appended
 * as visually-hidden text (not aria-label) so the link's accessible name keeps
 * the card content (caption, counts) and ends with where it goes.
 */
function PostLink({ href, label, children }: PostLinkProps) {
  if (!href) return <>{children}</>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      draggable={false}
      className="block text-inherit no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
    >
      {children}
      <span className="sr-only"> {label}</span>
    </a>
  );
}
